import DOMPurify from "dompurify";

interface SafeHtmlProps {
  html: string;
  className?: string;
  /**
   * Set when `html` was already sanitized by trusted server-side code (e.g.
   * renderTiptapHtml, which runs sanitize-html before this ever renders) —
   * skips re-sanitizing. Required for any content rendered during SSR: real
   * DOMPurify needs an actual browser DOM and throws without one, so a
   * component rendered server-side must either be trusted or client-only.
   */
  trusted?: boolean;
}

export function SafeHtml({ html, className, trusted = false }: SafeHtmlProps) {
  const safeHtml = trusted ? html : sanitizeUntrusted(html);
  return (
    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized above (or pre-sanitized via trusted)
    <div className={className} dangerouslySetInnerHTML={{ __html: safeHtml }} />
  );
}

function sanitizeUntrusted(html: string): string {
  if (typeof window === "undefined") {
    throw new Error(
      "SafeHtml: cannot sanitize untrusted HTML outside a browser (e.g. during SSR). " +
        "Either pre-sanitize server-side and pass trusted, or ensure this only renders client-side.",
    );
  }
  return DOMPurify.sanitize(html);
}
